import _ from 'lodash';
import { first, switchMap, tap } from 'rxjs/operators';

import { AuditCampaignOfflineDataFetcher, OfflineDataFetcher } from '@skinsoft/data-store';
import { EntityDataStore, LinkableItem } from '@skinsoft/entity-data-store';

import { auditCampaignSelectors } from '.';
import { Record, RecordDataStore } from '../record';

export interface AuditCampaign {
    id: string;
    workflowSchema: {
        linkableItems: LinkableItem[]
    },
    auditProjectSchema: {
        auditReports: string[]
    }
}

export class AuditCampaignDataStore extends EntityDataStore<AuditCampaign>{

    public static getInstance() {
        if (!AuditCampaignDataStore.instance) {
            AuditCampaignDataStore.instance = new AuditCampaignDataStore();
        }

        return AuditCampaignDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$auditCampaignDataStore = AuditCampaignDataStore.getInstance();
    }

    private static instance: AuditCampaignDataStore = null as any;

    constructor() {
        super('audit-projects', ['auditProject']);
    }

    public update(id: string, body: AuditCampaign, endpointOverride?: string) {
        if (window.navigator.onLine) {
            // Saving records with local ids
            const auditRecordsBeforeUpdate = auditCampaignSelectors.auditRecords(body).sort();

            // Removing temporary record ids to avoid Error 500 on update
            body.auditProjectSchema.auditReports = auditCampaignSelectors.auditRecords(body).filter(recordId => !OfflineDataFetcher.isTemporaryId(recordId))

            // Updating campaign to create missing records from linkable items
            return super.update(id, body, endpointOverride).pipe(
                switchMap(response => this.fetchOne(id)),
                tap(
                    response => {
                        // Retieving records, including the new ones freshly created
                        const auditRecordsAfterUpdate = auditCampaignSelectors.auditRecords(response).sort();

                        // Getting the created records that are not in cache yet (? perfs with a lot of records ?)
                        const diff = auditRecordsAfterUpdate.filter(
                            recordId => !auditRecordsBeforeUpdate.includes(recordId)
                        )

                        // Replacing temp ids in local records and pushing them to db
                        Promise.all(auditRecordsBeforeUpdate
                            .filter(recordId => OfflineDataFetcher.isTemporaryId(recordId)) // keep only ids of locally created entities
                            .map(
                                async (recordId, index) => {
                                    const dbId = diff[index]
                                    const updateBody = await RecordDataStore.getInstance().getObjectInUpdateCache(recordId);
                                    const existingBody = await RecordDataStore.getInstance().fetchOne(dbId).pipe(first()).toPromise();

                                    // Replacing  temporary id in body
                                    updateBody.id = dbId;

                                    // Keeping readonly fields to avoid error on update
                                    _.set(updateBody, 'recolementIdentificationSchema.recoleur', _.get(existingBody, 'recolementIdentificationSchema.recoleur'));
                                    _.set(updateBody, 'recolementIdentificationSchema.authorizedAuditor', _.get(existingBody, 'recolementIdentificationSchema.authorizedAuditor'));
                                    _.set(updateBody, 'institutionSharing', _.get(existingBody, 'institutionSharing'));

                                    RecordDataStore.getInstance().update(dbId, updateBody).subscribe(
                                        saveResponse => {
                                            // Removing freshly saved record from cache
                                            RecordDataStore.getInstance().removeObjectFromUpdateCache(recordId);
                                        }
                                    );
                                }
                            ))
                    })
            );
        }
        return super.update(id, body, endpointOverride);
    }

    public async addItem(
        auditCampaign: AuditCampaign,
        newItemId: string,
        itemType: string
    ): Promise<any> {
        const linkableItems = auditCampaignSelectors.linkableItems(auditCampaign);
        const newLinkedItem = {
            linkableItem: newItemId,
            linkableItemReferenceType: itemType
        }
        linkableItems.push(newLinkedItem)

        auditCampaign.workflowSchema.linkableItems = linkableItems;

        // Creating local record if offline mode 
        if (!window.navigator.onLine) {
            const newRecord = await this.cloneRecord(auditCampaign.id, newLinkedItem);
            if (newRecord) {
                auditCampaign.auditProjectSchema.auditReports.push(newRecord.id)
            }
        }

        return await this.update(auditCampaign.id, auditCampaign)
            .pipe(first())
            .toPromise();
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new AuditCampaignOfflineDataFetcher(this.sanitizedIdentifer);
    }

    /**
     * Creates a blank record from an existing one
     * @param campaignId 
     * @param itemId 
     */
    private async cloneRecord(campaignId: string, linkedItem: any) {
        const campaign = await this.fetchOne(campaignId).pipe(first()).toPromise();
        const recordToClone = await RecordDataStore.getInstance().fetchRandomRecord().pipe(first()).toPromise();
        const blankRecord: Record = await this.resetRecord(recordToClone, campaign);
        _.set(blankRecord, 'recolementLinkSchema.linkedItem', linkedItem)

        return RecordDataStore.getInstance().create({}, blankRecord).pipe(first()).toPromise();
    }

    private async resetRecord(record: Record, campaign: AuditCampaign) {
        // Removing identifiers to avoid doubles and conflicts (409)
        _.set(record, 'recolementIdentificationSchema.numberIdentifier', "");
        _.set(record, 'title', "");
        return record;
    }
}